import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Gift, 
  Star, 
  TrendingUp, 
  Award,
  ShoppingCart,
  Sparkles,
  Trophy,
  Zap,
  Tag,
  Clock,
  CheckCircle,
  Filter,
  Plus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const EnhancedRewards = () => {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [userPoints, setUserPoints] = useState(2450);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points: '',
    category: 'gift-cards',
    available: ''
  });

  const handleAddReward = async () => {
    if (!formData.name || !formData.points || !formData.available) {
      alert('Please fill in required fields: Name, Points, Available Quantity');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rewards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          points_required: parseInt(formData.points),
          category: formData.category,
          available_quantity: parseInt(formData.available)
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Reward added successfully!');
        setShowAddModal(false);
        setFormData({
          name: '',
          description: '',
          points: '',
          category: 'gift-cards',
          available: ''
        });
      }
    } catch (error) {
      console.error('Error adding reward:', error);
      alert('Error adding reward');
    }
  };

  const categories = [
    { id: 'all', name: 'All Rewards', icon: <Gift className="h-4 w-4" /> },
    { id: 'gift-cards', name: 'Gift Cards', icon: <ShoppingCart className="h-4 w-4" /> },
    { id: 'experiences', name: 'Experiences', icon: <Sparkles className="h-4 w-4" /> },
    { id: 'equipment', name: 'Equipment', icon: <Award className="h-4 w-4" /> },
    { id: 'courses', name: 'CE Courses', icon: <Star className="h-4 w-4" /> }
  ];

  const rewards = [
    {
      id: 1,
      name: '$50 Amazon Gift Card',
      points: 500,
      category: 'gift-cards',
      available: 25,
      redeemed: 12,
      image: '🎁',
      description: 'Redeemable for anything on Amazon'
    },
    {
      id: 2,
      name: '$100 Restaurant Voucher',
      points: 1000,
      category: 'gift-cards',
      available: 15,
      redeemed: 8,
      image: '🍽️',
      description: 'Fine dining at local restaurants'
    },
    {
      id: 3,
      name: 'Premium CE Course Access',
      points: 1500,
      category: 'courses',
      available: 50,
      redeemed: 23,
      image: '📚',
      description: 'Full access to premium continuing education'
    },
    {
      id: 4,
      name: 'Golf Day Experience',
      points: 2000,
      category: 'experiences',
      available: 10,
      redeemed: 5,
      image: '⛳',
      description: 'Premium golf course day with lunch'
    },
    {
      id: 5,
      name: 'Surgical Loupes',
      points: 3000,
      category: 'equipment',
      available: 8,
      redeemed: 3,
      image: '🔬',
      description: 'Professional grade surgical loupes'
    },
    {
      id: 6,
      name: 'Conference Ticket',
      points: 2500,
      category: 'experiences',
      available: 20,
      redeemed: 11,
      image: '🎫',
      description: 'Annual Dental Conference all-access pass'
    },
    {
      id: 7,
      name: 'iPad Pro',
      points: 5000,
      category: 'equipment',
      available: 5,
      redeemed: 2,
      image: '💻',
      description: 'Latest iPad Pro for digital workflows'
    },
    {
      id: 8,
      name: '$25 Starbucks Card',
      points: 250,
      category: 'gift-cards',
      available: 50,
      redeemed: 34,
      image: '☕',
      description: 'Perfect for your morning coffee'
    }
  ];

  const recentRedemptions = [
    { user: 'Dr. Sarah Johnson', reward: '$100 Restaurant Voucher', points: 1000, date: '2025-11-07' },
    { user: 'Dr. Michael Chen', reward: 'Premium CE Course Access', points: 1500, date: '2025-11-06' },
    { user: 'Dr. Gerard Nikodem', reward: 'Conference Ticket', points: 2500, date: '2025-11-05' },
    { user: 'Dr. David Schertzer', reward: '$50 Amazon Gift Card', points: 500, date: '2025-11-04' }
  ];

  const pointsHistory = [
    { action: 'Referral Completed', points: 500, date: '2025-11-08', type: 'earned' },
    { action: 'Patient Follow-up', points: 100, date: '2025-11-07', type: 'earned' },
    { action: 'Redeemed: $50 Gift Card', points: -500, date: '2025-11-06', type: 'redeemed' },
    { action: 'Monthly Bonus', points: 250, date: '2025-11-01', type: 'earned' },
    { action: 'Challenge Completed', points: 300, date: '2025-10-28', type: 'earned' }
  ];

  const filteredRewards = selectedCategory === 'all' 
    ? rewards 
    : rewards.filter(r => r.category === selectedCategory);

  const canRedeem = (points) => userPoints >= points;

  const handleRedeem = (reward) => {
    setSelectedReward(reward);
    setShowRedeemModal(true);
  };

  const confirmRedeem = () => {
    if (selectedReward && canRedeem(selectedReward.points)) {
      setUserPoints(userPoints - selectedReward.points);
      setShowRedeemModal(false);
      // In production, call API to redeem reward
      alert(`Successfully redeemed ${selectedReward.name}!`);
    }
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
            <Gift className="h-8 w-8 mr-3 text-[#D4AF37]" />
            Rewards Store
          </h1>
          <p className="text-gray-600">Redeem your points for exclusive rewards and experiences</p>
        </div>
        {(user?.role === 'admin' || user?.role === 'superadmin') && (
          <Button
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            onClick={() => setShowAddModal(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Reward
          </Button>
        )}
      </div>

      {/* Points Balance */}
      <Card className="mb-8 bg-gradient-to-r from-[#0A1628] to-[#1a2332] text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#D4AF37] text-sm mb-2 flex items-center">
                <Sparkles className="h-4 w-4 mr-2" />
                Your Points Balance
              </p>
              <p className="text-5xl font-bold">{userPoints.toLocaleString()}</p>
              <p className="text-sm mt-2 opacity-80">Rank: Gold Member</p>
            </div>
            <div className="text-right">
              <Trophy className="h-20 w-20 text-[#D4AF37] mb-2" />
              <Button className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]">
                View History
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Category Filter */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory(category.id)}
                    className={selectedCategory === category.id ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
                  >
                    {category.icon}
                    <span className="ml-2">{category.name}</span>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Rewards Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {filteredRewards.map((reward) => {
              const canAfford = canRedeem(reward.points);
              return (
                <Card key={reward.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="text-center mb-4">
                      <div className="text-6xl mb-3">{reward.image}</div>
                      <h3 className="font-semibold text-lg text-[#0A1628] mb-2">{reward.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{reward.description}</p>
                      <div className="flex items-center justify-center space-x-2 mb-3">
                        <Star className="h-5 w-5 text-[#D4AF37]" />
                        <span className="text-2xl font-bold text-[#D4AF37]">{reward.points}</span>
                        <span className="text-gray-600">points</span>
                      </div>
                      <div className="flex items-center justify-center space-x-4 text-sm text-gray-600 mb-4">
                        <span>{reward.available} available</span>
                        <span>•</span>
                        <span>{reward.redeemed} redeemed</span>
                      </div>
                      <Button
                        onClick={() => handleRedeem(reward)}
                        disabled={!canAfford}
                        className={canAfford 
                          ? 'w-full bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]' 
                          : 'w-full bg-gray-300 text-gray-500 cursor-not-allowed'}
                      >
                        {canAfford ? (
                          <>
                            <ShoppingCart className="h-4 w-4 mr-2" />
                            Redeem Now
                          </>
                        ) : (
                          <>
                            <Zap className="h-4 w-4 mr-2" />
                            Need {reward.points - userPoints} more points
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Points Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Points Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Earned</span>
                <span className="font-semibold text-green-600">+5,200</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Redeemed</span>
                <span className="font-semibold text-red-600">-2,750</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-sm font-medium">Current Balance</span>
                <span className="font-bold text-[#D4AF37]">{userPoints}</span>
              </div>
            </CardContent>
          </Card>

          {/* Recent Redemptions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Redemptions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentRedemptions.map((redemption, index) => (
                <div key={index} className="text-sm">
                  <p className="font-medium text-[#0A1628]">{redemption.user}</p>
                  <p className="text-gray-600">{redemption.reward}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-500">{redemption.date}</span>
                    <span className="text-xs font-semibold text-[#D4AF37]">{redemption.points} pts</span>
                  </div>
                  {index < recentRedemptions.length - 1 && <hr className="my-3" />}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* How to Earn */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                How to Earn Points
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span>Complete a referral</span>
                <Badge className="bg-blue-100 text-blue-800">+500</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span>Finish a challenge</span>
                <Badge className="bg-blue-100 text-blue-800">+300</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span>Patient follow-up</span>
                <Badge className="bg-blue-100 text-blue-800">+100</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span>Monthly bonus</span>
                <Badge className="bg-blue-100 text-blue-800">+250</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Redeem Modal */}
      {showRedeemModal && selectedReward && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Confirm Redemption</h2>
            <div className="text-center mb-6">
              <div className="text-6xl mb-3">{selectedReward.image}</div>
              <h3 className="font-semibold text-lg mb-2">{selectedReward.name}</h3>
              <p className="text-sm text-gray-600 mb-4">{selectedReward.description}</p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span>Cost:</span>
                  <span className="font-bold text-[#D4AF37]">{selectedReward.points} points</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span>Your Balance:</span>
                  <span className="font-bold">{userPoints} points</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span>After Redemption:</span>
                  <span className="font-bold text-green-600">{userPoints - selectedReward.points} points</span>
                </div>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button 
                onClick={confirmRedeem}
                className="flex-1 bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm Redemption
              </Button>
              <Button 
                onClick={() => setShowRedeemModal(false)} 
                variant="outline" 
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Reward Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Add New Reward</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Reward Name *</label>
                <Input
                  placeholder="$50 Amazon Gift Card"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea
                  placeholder="Describe the reward..."
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Points Required *</label>
                  <Input
                    type="number"
                    placeholder="500"
                    value={formData.points}
                    onChange={(e) => setFormData({...formData, points: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Available Quantity *</label>
                  <Input
                    type="number"
                    placeholder="25"
                    value={formData.available}
                    onChange={(e) => setFormData({...formData, available: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    className="w-full px-3 py-2 border rounded-md"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    <option value="gift-cards">Gift Cards</option>
                    <option value="experiences">Experiences</option>
                    <option value="equipment">Equipment</option>
                    <option value="courses">CE Courses</option>
                  </select>
                </div>
              </div>

              <div className="flex space-x-2 pt-4">
                <Button 
                  className="flex-1 bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                  onClick={handleAddReward}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Reward
                </Button>
                <Button 
                  onClick={() => setShowAddModal(false)} 
                  variant="outline" 
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default EnhancedRewards;